// src/pages/AgencyBranding.js
// « Ma marque » (plan Enterprise) : nom, logo, couleur et coordonnées utilisés sur les PDF et les liens de partage.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, Upload, Trash2, Save, Loader2, Lock } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';
const input = 'w-full bg-black border border-zinc-800 p-3 text-white font-mono text-sm focus:border-purple-500 outline-none placeholder:text-zinc-700';
const MAX_SIDE = 600;

// Redimensionne et convertit en PNG : un format que le PDF sait toujours intégrer, et un poids raisonnable.
function toPngDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('lecture'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('image'));
      img.onload = () => {
        const ratio = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function AgencyBranding() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const allowed = user?.subscription_plan === 'enterprise';
  const fileRef = useRef(null);
  const [form, setForm] = useState({ company_name: '', primary_color: '#3b82f6', contact_email: '', website: '', logo: '' });
  const [logoChanged, setLogoChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    axios.get(`${API_URL}/api/agency/branding`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setForm(r.data))
      .catch(() => toast.error('Impossible de charger votre marque'))
      .finally(() => setLoading(false));
  }, [allowed, token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) return toast.error('Choisissez une image PNG, JPEG ou WebP');
    try {
      const dataUrl = await toPngDataUrl(file);
      if (dataUrl.length > 400 * 1024) return toast.error('Image trop lourde même après réduction : essayez une version plus simple du logo');
      setForm((f) => ({ ...f, logo: dataUrl }));
      setLogoChanged(true);
    } catch {
      toast.error('Image illisible');
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, logo: logoChanged ? form.logo : null };
      const r = await axios.put(`${API_URL}/api/agency/branding`, body, { headers: { Authorization: `Bearer ${token}` } });
      setForm(r.data);
      setLogoChanged(false);
      toast.success('Marque enregistrée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const color = /^#[0-9a-fA-F]{6}$/.test(form.primary_color) ? form.primary_color : '#3b82f6';

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">Ma marque</h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
            Votre nom, votre logo et votre couleur remplacent ceux de CyberGuard sur les rapports PDF et les pages de partage que vous envoyez à vos clients.
          </p>
        </div>

        {!allowed ? (
          <div className="bg-zinc-900 border border-zinc-800 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Lock className="w-6 h-6 text-zinc-500 shrink-0" />
              <div>
                <p className="text-white font-mono font-bold text-sm">Marque blanche — plan Enterprise</p>
                <p className="text-zinc-500 text-xs mt-1">Rapports à votre logo, fiches clients et liens de partage pour vos clients.</p>
              </div>
            </div>
            <button onClick={() => navigate('/billing')} className="bg-purple-600 hover:bg-purple-700 px-5 py-2 font-mono text-xs uppercase text-white shrink-0">Voir les plans →</button>
          </div>
        ) : loading ? (
          <p className="text-zinc-500 font-mono text-xs">Chargement…</p>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            <form onSubmit={save} className="bg-zinc-900 border border-zinc-800 p-6 space-y-4">
              <label className="block space-y-1">
                <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Nom de votre agence</span>
                <input value={form.company_name} onChange={set('company_name')} maxLength={80} placeholder="Agence Web Lyon" className={input} />
              </label>
              <div className="space-y-1">
                <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Logo</span>
                <div className="flex items-center gap-3">
                  <div className="w-28 h-14 bg-white flex items-center justify-center border border-zinc-700 overflow-hidden">
                    {form.logo ? <img src={form.logo} alt="Logo" className="max-w-full max-h-full object-contain" /> : <span className="text-zinc-400 text-[10px] font-mono">Aucun logo</span>}
                  </div>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={pickLogo} className="hidden" aria-label="Choisir un logo" />
                  <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 border border-zinc-700 hover:border-purple-500 px-3 py-2 font-mono text-[10px] uppercase text-zinc-300">
                    <Upload className="w-3 h-3" /> Choisir
                  </button>
                  {form.logo && (
                    <button type="button" onClick={() => { setForm((f) => ({ ...f, logo: '' })); setLogoChanged(true); }} className="p-2 border border-zinc-800 hover:border-red-500 text-zinc-500 hover:text-red-400" aria-label="Retirer le logo">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-zinc-600 text-xs">PNG, JPEG ou WebP. Un logo sur fond transparent ou blanc rend le mieux.</p>
              </div>
              <label className="block space-y-1">
                <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Couleur principale</span>
                <div className="flex gap-2">
                  <input type="color" value={color} onChange={set('primary_color')} className="w-12 h-12 bg-black border border-zinc-800 cursor-pointer" aria-label="Couleur" />
                  <input value={form.primary_color} onChange={set('primary_color')} maxLength={7} className={`${input} uppercase`} />
                </div>
              </label>
              <label className="block space-y-1">
                <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Email de contact</span>
                <input type="email" value={form.contact_email} onChange={set('contact_email')} placeholder="contact@agence.fr" className={input} />
              </label>
              <label className="block space-y-1">
                <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Site web</span>
                <input value={form.website} onChange={set('website')} placeholder="https://agence.fr" className={input} />
              </label>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 px-5 py-3 font-mono text-xs uppercase tracking-widest text-white">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer
              </button>
            </form>

            {/* Aperçu de l'en-tête des rapports */}
            <div className="space-y-2">
              <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest flex items-center gap-2"><Palette className="w-3 h-3" /> Aperçu</p>
              <div className="bg-white text-zinc-900 shadow-xl" data-testid="branding-preview">
                <div style={{ height: 6, background: color }} />
                <div className="p-5 flex items-start justify-between gap-4">
                  {form.logo
                    ? <img src={form.logo} alt="" className="max-h-12 max-w-[160px] object-contain" />
                    : <span className="font-bold text-xl" style={{ color }}>{form.company_name || 'CyberGuard'}</span>}
                  <div className="text-right text-[11px] text-zinc-500">
                    <div>Rapport d'audit de sécurité</div>
                    {form.logo && form.company_name && <div>{form.company_name}</div>}
                  </div>
                </div>
                <div className="px-5 pb-5 space-y-3">
                  <div className="text-lg font-bold">client-exemple.fr</div>
                  <div className="text-white text-xs font-bold px-3 py-2" style={{ background: color }}>Sécurité du site</div>
                  <div className="text-xs text-zinc-600 border-b border-zinc-200 pb-2">OK — Certificat SSL valide</div>
                  <div className="text-[10px] text-zinc-400 pt-4 border-t border-zinc-200">
                    {[form.company_name || 'CyberGuard', form.contact_email, form.website].filter(Boolean).join('  ·  ')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
